/**
 * Dashboard utility functions for shared logic across components
 */

/**
 * Converts hex color to rgba format with specified alpha
 * @param {string} hex - Hex color code (e.g., "#1565C0")
 * @param {number} alpha - Alpha transparency (0-1)
 * @returns {string} RGBA color string
 */
export function hexToRgba(hex, alpha) {
  if (!hex || typeof hex !== 'string' || !hex.startsWith('#')) {
    return `rgba(0, 0, 0, ${alpha})`;
  }

  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);

  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/**
 * Formats currency values with optional short format
 * @param {number} value - Numeric value to format
 * @param {boolean} short - Whether to use short format (K for thousands)
 * @returns {string} Formatted currency string
 */
export function formatCurrency(value, short = false) {
  if (short && value >= 1000) {
    return `$${(value / 1000).toFixed(0)}k`;
  }

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0
  }).format(value);
}

/**
 * Formats status names from snake_case to Title Case
 * @param {string} status - Status string (e.g., "in_transit")
 * @returns {string} Formatted status name (e.g., "In Transit")
 */
export function formatStatusName(status) {
  if (!status || typeof status !== 'string') {
    return 'Unknown';
  }

  return status
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Gets status color based on status key
 * @param {string} status - Status key
 * @returns {string} Hex color code
 */
export function getStatusColor(status) {
  const statusColors = {
    quoted: '#1565C0',
    assigned: '#E65100',
    in_transit: '#0288D1',
    delivered: '#2E7D32'
  };

  return statusColors[status] || '#666666';
}

/**
 * Gets ranking color based on index
 * @param {number} index - Index for ranking color
 * @returns {string} Hex color code
 */
export function getRankingColor(index) {
  const rankingColors = [
    '#E65100',
    '#2E7D32',
    '#1565C0',
    '#C2185B',
    '#7B1FA2',
    '#F57C00',
    '#00796B',
    '#4527A0',
    '#D32F2F',
    '#388E3C'
  ];

  return rankingColors[index % rankingColors.length];
}

/**
 * Calculates percentage for progress bars
 * @param {number} value - Current value
 * @param {number} maxValue - Maximum value
 * @returns {number} Percentage (0-100)
 */
export function calculatePercentage(value, maxValue) {
  if (maxValue === 0) return 0;
  return Math.round((value / maxValue) * 100);
}
